import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '../../config/api';
import { TailorSelection } from './TailorSelection';
import { EnhancedChatInterface } from './EnhancedChatInterface';
import { cacheHelpers } from '@/services/sessionCache';

export function MessagingPage() {
  console.log('MessagingPage component initialized');
  const { user } = useAuth();
  const token = localStorage.getItem('authToken');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [assignedTailor, setAssignedTailor] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTailorSelection, setShowTailorSelection] = useState(false);
  const hasEnsuredRef = useRef(false);

  // Ensure conversation exists for customer-tailor pair
  useEffect(() => {
    console.log('MessagingPage useEffect triggered');
    console.log('User:', user);
    console.log('Token:', token ? 'exists' : 'missing');
    console.log('User role:', user?.role);
    
    if (!user || !token || user.role !== 'customer') {
      console.log('useEffect condition failed - not proceeding with conversation creation');
      return;
    }

    // Prevent double posts in React StrictMode or component re-mounts
    if (hasEnsuredRef.current) {
      console.log('Conversation already being ensured, skipping');
      return;
    }
    hasEnsuredRef.current = true;

    const ensureConversation = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('Getting assigned tailor for customer:', user.id);
        // Get assigned tailor (this would come from your existing user/tailor assignment logic)
        const tailorData = await getAssignedTailorData();
        console.log('Assigned tailor data:', tailorData);

        if (!tailorData || !tailorData.id) {
          // No tailor found, show tailor selection
          setShowTailorSelection(true);
          setIsLoading(false);
          return;
        }

        setAssignedTailor(tailorData);

        // Ensure conversation exists
        console.log('Creating conversation for customer:', user.id, 'and tailor:', tailorData.id);
        
        // Send as strings since backend expects string validation
        const body = JSON.stringify({
          customerId: String(user.id),
          tailorId: String(tailorData.id)
        });
        console.log('Request body:', body);
        
        const response = await fetch(`${API_BASE}/api/chat/conversations/ensure`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: body
        });

        console.log('Conversation creation response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to create conversation:', errorText);
          
          // Better error handling for different status codes
          if (response.status >= 500) {
            throw new Error(`Server error ${response.status}: ${errorText}`);
          } else if (response.status === 409) {
            throw new Error(`Conversation already exists: ${errorText}`);
          } else if (response.status === 422) {
            throw new Error(`Invalid request: ${errorText}`);
          } else {
            throw new Error(`Request failed ${response.status}: ${errorText}`);
          }
        }

        const conversationResponse = await response.json();
        console.log('Created conversation:', conversationResponse);
        setConversationId(conversationResponse.conversationId);
      } catch (_err) {
        console.error('Failed to ensure conversation:', err);
        setError(err instanceof Error ? err.message : 'Failed to create conversation');
      } finally {
        setIsLoading(false);
      }
    };

    ensureConversation();
  }, [user, token]);

  const handleTailorSelect = async (tailor: any) => {
    try {
      setIsLoading(true);
      setError(null);
      setAssignedTailor(tailor);
      setShowTailorSelection(false);

      // Create conversation with selected tailor
      const body = JSON.stringify({
        customerId: String(user?.id),
        tailorId: String(tailor.id)
      });

      const response = await fetch(`${API_BASE}/api/chat/conversations/ensure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: body
      });

      if (!response.ok) {
        throw new Error(`Failed to create conversation: ${response.status}`);
      }

      const conversationResponse = await response.json();
      setConversationId(conversationResponse.conversationId);
    } catch (_err) {
      console.error('Failed to create conversation with selected tailor:', err);
      setError(err instanceof Error ? err.message : 'Failed to create conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelTailorSelection = () => {
    setShowTailorSelection(false);
    setIsLoading(false);
  };

  // Get assigned tailor data from user's orders or create default assignment
  const getAssignedTailorData = async (): Promise<any | null> => {
    try {
      // Check cache first
      const cachedTailors = cacheHelpers.getTailors(user?.email || '');
      if (cachedTailors && cachedTailors.length > 0) {
        console.log('Using cached tailors:', cachedTailors);
        return { id: cachedTailors[0].id, name: cachedTailors[0].name || 'Your Assigned Tailor' };
      }

      // First, try to get a tailor from existing orders
      const ordersResponse = await fetch(`${API_BASE}/api/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (ordersResponse.ok) {
        const orders = await ordersResponse.json();
        console.log('Orders API response:', orders);
        console.log('Orders type:', typeof orders, 'Is array:', Array.isArray(orders));
        
        // Handle both array and object responses gracefully
        const ordersArray = Array.isArray(orders) ? orders : (orders?.orders || orders?.data || []);
        console.log('Orders array length:', ordersArray.length);
        
        if (Array.isArray(ordersArray)) {
          const customerOrders = ordersArray.filter((order: any) => order.customer_id === user?.id);
          console.log('Found customer orders:', customerOrders.length);
          if (customerOrders.length > 0 && customerOrders[0].tailor_id) {
            console.log('Found tailor from orders:', customerOrders[0].tailor_id);
            return { id: customerOrders[0].tailor_id, name: 'Your Assigned Tailor' };
          }
        }
      }

      // If no orders found, try to get any available tailor
      const tailorsResponse = await fetch(`${API_BASE}/api/appointments/available-tailors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (tailorsResponse.ok) {
        const tailors = await tailorsResponse.json();
        console.log('Tailors API response:', tailors);
        console.log('Tailors type:', typeof tailors, 'Is array:', Array.isArray(tailors));
        
        let tailorsArray = [];
        if (Array.isArray(tailors) && tailors.length > 0) {
          tailorsArray = tailors;
          console.log('Found tailor from available tailors:', tailors[0].id);
          const result = { id: tailors[0].id, name: tailors[0].name || 'Available Tailor' };
          // Cache the tailors
          cacheHelpers.setTailors(user?.email || '', tailorsArray);
          return result;
        } else if (tailors.tailors && Array.isArray(tailors.tailors) && tailors.tailors.length > 0) {
          tailorsArray = tailors.tailors;
          console.log('Found tailor from available tailors:', tailors.tailors[0].id);
          const result = { id: tailors.tailors[0].id, name: tailors.tailors[0].name || 'Available Tailor' };
          // Cache the tailors
          cacheHelpers.setTailors(user?.email || '', tailorsArray);
          return result;
        } else if (tailors.data && Array.isArray(tailors.data) && tailors.data.length > 0) {
          tailorsArray = tailors.data;
          console.log('Found tailor from available tailors:', tailors.data[0].id);
          const result = { id: tailors.data[0].id, name: tailors.data[0].name || 'Available Tailor' };
          // Cache the tailors
          cacheHelpers.setTailors(user?.email || '', tailorsArray);
          return result;
        }
      } else {
        console.log('Tailors API failed:', tailorsResponse.status, tailorsResponse.statusText);
      }

      // If no tailors found, return null to show error
      return null;
    } catch (_err) {
      console.error('Failed to get assigned tailor:', err);
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messaging...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-lg font-medium">Unable to start messaging</p>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (showTailorSelection) {
    return (
      <TailorSelection 
        onTailorSelect={handleTailorSelect}
        onCancel={handleCancelTailorSelection}
      />
    );
  }

  if (!conversationId || !assignedTailor) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="text-lg font-medium">No conversation available</p>
            <p className="text-sm text-gray-600 mt-2">Please contact support to get started</p>
          </div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Messaging</h2>
            <p className="text-gray-600 mt-1">
              Chat with {assignedTailor?.name || 'your assigned tailor'}
            </p>
          </div>
        </div>

        <EnhancedChatInterface 
          conversationId={conversationId}
          tailor={assignedTailor}
          currentUserId={user?.id || 0}
        />
      </div>
    </div>
  );
}
